import { useEffect, useRef, useState } from 'react';
import RDP from 'react-datepicker';
import dayjs, { type Dayjs } from 'dayjs';
import { CalendarDays, ChevronDown, ChevronUp } from 'lucide-react';
import { Button, Popover, Select, cn } from '../../ui';

/**
 * WHEN A MEET HAPPENS — one field, one compact panel: the calendar and the time side by side.
 *
 * History (2026-09-28): the form used a RangePicker with `showTime` — a 48-row scroll list wedged
 * beside the calendar that opened at midnight, silently set 12:00 AM when you only clicked a day, and
 * made you open a second full date+time popup for the end. Two rounds of dropdown-based redesigns were
 * rejected ("hard to use", "not looking good"). The owner then pointed at a classic picker — calendar,
 * then hour : minute with ▲▼ steppers and an AM/PM toggle — and this follows it, adapted for a meet:
 *  - no seconds, no 12/24 switch, no time zone: every meet is India time, planned to the minute at most;
 *  - the hour and minute boxes can also be TYPED into, so 9 → 2 PM is two keystrokes, not five clicks;
 *  - minutes step by 5 on the arrows (any minute can still be typed);
 *  - calendar and time SIDE BY SIDE (stacked, it overflowed the dialog), stacking again on a phone;
 *  - "Lasts" — No fixed finish, 1 hour … Full day, 1–3 days — or "Custom end date & time…", which
 *    shows a Starts / Ends switch: the calendar and the arrows then edit whichever is selected, and the
 *    days between are shaded. (No second calendar popup inside the panel: react-datepicker's popup is
 *    not a Radix layer, so clicking it would count as "outside" and close the panel.)
 *
 * The value in and out is unchanged — `[start, end | null]` — so the form's validation, prefill and
 * submit work as before. The field reports `null` until a day is picked; picking a day starts the time
 * at 10:00 AM, printed in the field and in the panel (never a hidden midnight).
 */
type Range = [Dayjs | null, Dayjs | null] | null | undefined;
/** How the end is set: none, a length from the list (minutes), or an exact custom end. */
type EndChoice = 'none' | 'custom' | number;

const DEFAULT_START = 10 * 60; // 10:00 AM — what a day starts at until the time is changed
const MIN_STEP = 5;
const LASTS: [number, string][] = [
  [60, '1 hour'], [90, '1 h 30 min'], [120, '2 hours'], [180, '3 hours'], [240, 'Half day (4 h)'],
  [360, '6 hours'], [480, 'Full day (8 h)'], [1440, '1 day'], [2 * 1440, '2 days'], [3 * 1440, '3 days'],
];
const NONE = -1;
const CUSTOM = -2;

const at = (day: Dayjs, min: number) => day.startOf('day').add(min, 'minute');
const minutesOf = (d: Dayjs) => d.hour() * 60 + d.minute();
const keyOf = (v: Range) => `${v?.[0]?.valueOf() ?? ''}|${v?.[1]?.valueOf() ?? ''}`;
const lasting = (mins: number) => {
  const d = Math.floor(mins / 1440); const h = Math.floor((mins % 1440) / 60); const m = mins % 60;
  return [d && `${d} ${d === 1 ? 'day' : 'days'}`, h && `${h} h`, m && `${m} min`].filter(Boolean).join(' ');
};

/** One ▲ box ▼ column. The box is a real input: click it and type. */
function Stepper({ value, onStep, onType, label, pad }: {
  value: number; onStep: (dir: 1 | -1) => void; onType: (n: number) => void; label: string; pad?: boolean;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (pad ? String(value).padStart(2, '0') : String(value));
  // Tinted green, not hairline-grey: the arrows are the main way to set the time, so they must read as
  // buttons at a glance (owner, 2026-09-28: "highlight these buttons properly").
  const arrow = 'grid h-7 w-12 place-items-center rounded-control border border-brand-500/45 bg-brand-500/10 text-brand-700 shadow-e1 transition-colors hover:border-brand-500 hover:bg-brand-500/20 active:bg-brand-500/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:bg-brand-400/15 dark:text-brand-300 dark:hover:bg-brand-400/25';
  const commit = () => { if (draft != null && /^\d{1,2}$/.test(draft)) onType(Number(draft)); setDraft(null); };
  return (
    <div className="flex flex-col items-center gap-1">
      <button type="button" className={arrow} aria-label={`${label} up`} onClick={() => onStep(1)}><ChevronUp size={18} strokeWidth={2.5} /></button>
      <input
        value={shown} inputMode="numeric" aria-label={label} maxLength={2}
        onFocus={(e) => e.target.select()}
        onChange={(e) => setDraft(e.target.value.replace(/\D/g, ''))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'ArrowUp') { e.preventDefault(); onStep(1); }
          if (e.key === 'ArrowDown') { e.preventDefault(); onStep(-1); }
        }}
        className="h-10 w-12 rounded-control border border-line bg-surface text-center text-lg font-semibold tabular-nums text-content focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
      />
      <button type="button" className={arrow} aria-label={`${label} down`} onClick={() => onStep(-1)}><ChevronDown size={18} strokeWidth={2.5} /></button>
    </div>
  );
}

export function MeetWhenField({ value, onChange, disabled, id, 'aria-invalid': invalid }: {
  value?: Range;
  onChange?: (v: [Dayjs, Dayjs | null] | null) => void;
  disabled?: boolean;
  id?: string;
  'aria-invalid'?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Dayjs | null>(null);
  const [startMin, setStartMin] = useState(DEFAULT_START);
  const [choice, setChoice] = useState<EndChoice>('none');
  const [customEnd, setCustomEnd] = useState<Dayjs | null>(null); // only when choice === 'custom'
  const [editing, setEditing] = useState<'start' | 'end'>('start'); // what the calendar + arrows set
  const lastSent = useRef<string | null>(null);

  useEffect(() => {
    if (keyOf(value) === lastSent.current) return;
    const s = value?.[0] ? dayjs(value[0]) : null;
    const e = value?.[1] ? dayjs(value[1]) : null;
    setDate(s ? s.startOf('day') : null);
    setStartMin(s ? minutesOf(s) : DEFAULT_START);
    setEditing('start');
    if (!s || !e) { setChoice('none'); setCustomEnd(null); return; }
    const span = e.diff(s, 'minute');
    // A length from the list reads back as that choice; anything else is an exact custom end.
    if (LASTS.some(([m]) => m === span)) { setChoice(span); setCustomEnd(null); }
    else { setChoice('custom'); setCustomEnd(e); }
  }, [keyOf(value)]); // eslint-disable-line react-hooks/exhaustive-deps

  const build = (d: Dayjs | null, sm: number, c: EndChoice, ce: Dayjs | null): [Dayjs, Dayjs | null] | null => {
    if (!d) return null;
    const s = at(d, sm);
    return [s, c === 'none' ? null : c === 'custom' ? ce : s.add(c, 'minute')];
  };
  const emit = (d: Dayjs | null, sm: number, c: EndChoice, ce: Dayjs | null) => {
    const next = build(d, sm, c, ce);
    lastSent.current = keyOf(next);
    onChange?.(next);
  };

  const start = date ? at(date, startMin) : null;
  const end = build(date, startMin, choice, customEnd)?.[1] ?? null;
  const endBeforeStart = !!(start && end && !end.isAfter(start));
  const onEnd = choice === 'custom' && editing === 'end';

  // The time the arrows are showing: the start, or the custom end while "Ends" is selected.
  const shownMin = onEnd && customEnd ? minutesOf(customEnd) : startMin;
  const h24 = Math.floor(shownMin / 60);
  const minute = shownMin % 60;
  const pm = h24 >= 12;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const setTime = (m: number) => {
    const v = ((m % 1440) + 1440) % 1440;
    if (onEnd && customEnd) { const ce = at(customEnd, v); setCustomEnd(ce); emit(date, startMin, choice, ce); }
    else { setStartMin(v); emit(date, v, choice, customEnd); }
  };
  const pickDay = (d: Date | null) => {
    const day = d ? dayjs(d).startOf('day') : null;
    if (onEnd) { if (!day) return; const ce = at(day, customEnd ? minutesOf(customEnd) : startMin); setCustomEnd(ce); emit(date, startMin, choice, ce); }
    else { setDate(day); emit(day, startMin, choice, customEnd); }
  };
  const pickLasts = (v: number) => {
    if (v === CUSTOM) {
      // An end needs a start to be after; without a start day there is nothing to measure it from.
      if (!date) return;
      // Start the custom end two hours after the start, and hand the calendar + arrows over to it.
      const ce = end ?? (start ? start.add(2, 'hour') : null);
      setChoice('custom'); setCustomEnd(ce); setEditing('end'); emit(date, startMin, 'custom', ce);
      return;
    }
    const c: EndChoice = v === NONE ? 'none' : v;
    setChoice(c); setCustomEnd(null); setEditing('start'); emit(date, startMin, c, null);
  };

  const fieldText = start
    ? `${start.format('ddd, D MMM YYYY')} at ${start.format('h:mm A')}${end
      ? ` – ${end.isSame(start, 'day') ? end.format('h:mm A') : end.format('ddd D MMM, h:mm A')}`
      : ''}`
    : null;

  const lastsOptions = [
    { value: NONE, label: 'No fixed finish' },
    ...LASTS.map(([m, label]) => ({ value: m, label })),
    { value: CUSTOM, label: 'Custom end date & time…', disabled: !date },
  ];

  const toggle = (on: boolean) => cn(
    'h-9 w-12 rounded-control border text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30',
    on ? 'border-brand-600 bg-brand-600 text-white' : 'border-line bg-surface text-muted hover:border-brand-400 hover:text-brand-700 dark:hover:text-brand-300',
  );
  const which = (on: boolean) => cn(
    'flex min-w-0 flex-1 flex-col items-start rounded-control border px-2 py-1 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30',
    on ? 'border-brand-600 bg-brand-500/10' : 'border-line bg-surface hover:border-brand-400',
  );

  const panel = (
    // Calendar and time SIDE BY SIDE: stacked, the panel was ~620px tall and ran off the bottom of the
    // dialog (owner, 2026-09-28: "show the date and time side by side as it is overflowing"). On a
    // phone too narrow for both it stacks again and scrolls inside the room the popover has, so
    // "Lasts" and Done are never pushed off the screen (32px = the popover's padding plus a margin).
    <div
      className="flex flex-col gap-4 overflow-y-auto overscroll-contain text-content sm:flex-row sm:gap-0"
      style={{ maxHeight: 'calc(var(--radix-popover-content-available-height, 80vh) - 32px)' }}
    >
      {/* The calendar sits IN the panel, so it drops its own card frame (border, shadow, tinted head).
          `!` because theme.css styles .react-datepicker outside any @layer, which beats utilities. */}
      <div className="flex shrink-0 justify-center [&_.react-datepicker]:border-0! [&_.react-datepicker]:shadow-none! [&_.react-datepicker__header]:bg-transparent! sm:pr-4">
        <RDP
          inline
          selected={(onEnd ? customEnd : date)?.toDate() ?? null}
          // With a custom end the days between start and end are shaded, so the span is visible.
          startDate={choice === 'custom' && date ? date.toDate() : undefined}
          endDate={choice === 'custom' && customEnd ? customEnd.toDate() : undefined}
          selectsStart={choice === 'custom' && !onEnd}
          selectsEnd={onEnd}
          minDate={onEnd && date ? date.toDate() : undefined}
          onChange={(d: Date | null) => pickDay(d)}
        />
      </div>

      <div className="flex min-w-0 flex-col border-t border-dashed border-line pt-3 sm:w-66 sm:border-l sm:border-t-0 sm:pl-4 sm:pt-1">
        {choice === 'custom' ? (
          <div className="mb-2 flex gap-1.5" role="group" aria-label="Set the start or the end">
            <button type="button" aria-pressed={!onEnd} className={which(!onEnd)} onClick={() => setEditing('start')}>
              <span className="type-overline text-subtle">Starts</span>
              <span className="w-full truncate text-xs font-medium">{start ? start.format('D MMM, h:mm A') : 'pick a day'}</span>
            </button>
            <button type="button" aria-pressed={onEnd} className={which(onEnd)} onClick={() => setEditing('end')}>
              <span className="type-overline text-subtle">Ends</span>
              <span className={cn('w-full truncate text-xs font-medium', endBeforeStart && 'text-danger-600')}>{customEnd ? customEnd.format('D MMM, h:mm A') : '—'}</span>
            </button>
          </div>
        ) : (
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <span className="type-overline text-subtle">Starts at</span>
            <span className="type-caption text-subtle">{date ? date.format('ddd, D MMM') : 'pick a day'}</span>
          </div>
        )}
        <div className="flex items-center justify-center gap-2">
          <Stepper
            label="Hour" value={h12}
            onStep={(dir) => setTime(shownMin + dir * 60)}
            onType={(n) => { if (n >= 1 && n <= 12) setTime(((n % 12) + (pm ? 12 : 0)) * 60 + minute); }}
          />
          <span className="pb-0.5 text-lg font-semibold text-subtle">:</span>
          <Stepper
            label="Minute" value={minute} pad
            onStep={(dir) => setTime(h24 * 60 + (dir === 1 ? (Math.floor(minute / MIN_STEP) + 1) * MIN_STEP : (Math.ceil(minute / MIN_STEP) - 1) * MIN_STEP))}
            onType={(n) => { if (n >= 0 && n <= 59) setTime(h24 * 60 + n); }}
          />
          <div className="ml-1 flex flex-col gap-1.5">
            <button type="button" aria-pressed={!pm} className={toggle(!pm)} onClick={() => pm && setTime(shownMin - 720)}>AM</button>
            <button type="button" aria-pressed={pm} className={toggle(pm)} onClick={() => !pm && setTime(shownMin + 720)}>PM</button>
          </div>
        </div>

        <div className="mt-4">
          <div className="mb-1.5 type-overline text-subtle">Duration</div>
          <Select
            value={choice === 'none' ? NONE : choice === 'custom' ? CUSTOM : choice}
            aria-label="Duration" style={{ width: '100%' }} options={lastsOptions}
            onChange={(v: number) => pickLasts(v)}
          />
          {end && start && (
            <div className={cn('mt-1.5 type-caption', endBeforeStart ? 'text-danger-600' : 'text-subtle')}>
              {endBeforeStart
                ? 'Ends before it starts — pick a later end'
                : `Ends ${end.isSame(start, 'day') ? `at ${end.format('h:mm A')}` : end.format('ddd D MMM, h:mm A')} (${lasting(end.diff(start, 'minute'))})`}
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between gap-2 sm:mt-auto sm:pt-4">
          <span className="type-caption text-muted">{date ? '' : 'Pick a day first'}</span>
          <Button variant="primary" size="sm" onClick={() => setOpen(false)}>Done</Button>
        </div>
      </div>
    </div>
  );

  return (
    <Popover
      open={open && !disabled} onOpenChange={(o) => !disabled && setOpen(o)} placement="bottomLeft"
      content={panel} overlayClassName="max-w-[calc(100vw-24px)]"
    >
      <button
        type="button" id={id} data-meet-when disabled={disabled} aria-invalid={invalid} aria-haspopup="dialog" aria-expanded={open}
        className={cn(
          'flex h-9 w-full items-center gap-2 rounded-control border bg-surface pl-3 text-left text-sm text-content transition-colors',
          'hover:border-brand-300 focus-visible:border-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-60',
          invalid ? 'border-danger-500' : 'border-line',
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', !fieldText && 'text-subtle')}>{fieldText ?? 'Pick the date and time'}</span>
        {end && start && !endBeforeStart && <span className="shrink-0 type-caption text-subtle">{lasting(end.diff(start, 'minute'))}</span>}
        {/* The calendar button at the right edge, as in the reference design. */}
        <span className="grid h-full w-9 shrink-0 place-items-center rounded-r-control border-l border-line bg-brand-600 text-white">
          <CalendarDays size={16} />
        </span>
      </button>
    </Popover>
  );
}
